import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react";

interface MerchantRevenueDetail {
  merchantId: string;
  minimumConsumption: number;
  realConsumption: number;
  consumptionToUse: number;
  conversations: number;
  callMinutes: number;
  outbounds: number;
  conversationCost: number;
  callMinutesCost: number;
  outboundCost: number;
  billingPeriod: {
    startDate: string;
    endDate: string;
  };
}

interface RevenueDetailsTableProps {
  data: MerchantRevenueDetail[];
  trmValue: number;
}

type SortColumn = 
  | "merchantId" 
  | "conversations"
  | "callMinutes"
  | "outbounds"
  | "conversationCost"
  | "callMinutesCost"
  | "outboundCost"
  | "realConsumption"
  | "minimumConsumption"
  | "consumptionToUse"
  | "usdAmount"
  | "billingPeriod"
  | null;

type SortDirection = "asc" | "desc";

const RevenueDetailsTable = ({ data, trmValue }: RevenueDetailsTableProps) => {
  const [sortColumn, setSortColumn] = useState<SortColumn>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");

  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  const getSortedData = () => {
    if (!sortColumn) return data;

    return [...data].sort((a, b) => {
      let aValue: string | number;
      let bValue: string | number;

      switch (sortColumn) {
        case "merchantId":
          aValue = a.merchantId;
          bValue = b.merchantId;
          break;
        case "conversations":
          aValue = a.conversations;
          bValue = b.conversations;
          break;
        case "callMinutes":
          aValue = a.callMinutes;
          bValue = b.callMinutes;
          break;
        case "outbounds":
          aValue = a.outbounds;
          bValue = b.outbounds;
          break;
        case "conversationCost":
          aValue = a.conversationCost;
          bValue = b.conversationCost;
          break;
        case "callMinutesCost":
          aValue = a.callMinutesCost;
          bValue = b.callMinutesCost;
          break;
        case "outboundCost":
          aValue = a.outboundCost;
          bValue = b.outboundCost;
          break;
        case "realConsumption":
          aValue = a.realConsumption;
          bValue = b.realConsumption;
          break;
        case "minimumConsumption":
          aValue = a.minimumConsumption;
          bValue = b.minimumConsumption;
          break;
        case "consumptionToUse":
          aValue = a.consumptionToUse;
          bValue = b.consumptionToUse;
          break;
        case "usdAmount":
          aValue = a.consumptionToUse / trmValue;
          bValue = b.consumptionToUse / trmValue;
          break;
        case "billingPeriod":
          aValue = new Date(a.billingPeriod.startDate).getTime();
          bValue = new Date(b.billingPeriod.startDate).getTime();
          break;
        default:
          return 0;
      }

      if (typeof aValue === "string" && typeof bValue === "string") {
        const comparison = aValue.localeCompare(bValue);
        return sortDirection === "asc" ? comparison : -comparison;
      }

      if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
      if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
  };

  const renderSortIcon = (column: SortColumn) => {
    if (sortColumn !== column) {
      return <ArrowUpDown className="w-3 h-3 ml-1 inline text-gray-400" />;
    }
    return sortDirection === "asc" 
      ? <ArrowUp className="w-3 h-3 ml-1 inline text-blue-500" />
      : <ArrowDown className="w-3 h-3 ml-1 inline text-blue-500" />;
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("es-CO", {
      style: "currency",
      currency: "COP",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatUSD = (value: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatNumber = (num: number) => num.toLocaleString();

  const getConsumptionStatus = (realConsumption: number, minimumConsumption: number) => {
    if (realConsumption === 0) return { status: "No Usage", variant: "secondary" as const };
    if (realConsumption >= minimumConsumption) return { status: "Above Minimum", variant: "default" as const };
    return { status: "Below Minimum", variant: "outline" as const };
  };

  const columns = [
    { key: "merchantId", label: "Merchant ID", minWidth: "180px" },
    { key: "billingPeriod", label: "Billing Period", minWidth: "120px" },
    { key: "conversations", label: "Conversations", minWidth: "100px", numeric: true },
    { key: "callMinutes", label: "Call Minutes", minWidth: "100px", numeric: true },
    { key: "outbounds", label: "Outbounds", minWidth: "100px", numeric: true },
    { key: "conversationCost", label: "Conversation Cost", minWidth: "120px", numeric: true },
    { key: "callMinutesCost", label: "Call Cost", minWidth: "120px", numeric: true },
    { key: "outboundCost", label: "Outbound Cost", minWidth: "120px", numeric: true },
    { key: "realConsumption", label: "Real Consumption", minWidth: "120px", numeric: true },
    { key: "minimumConsumption", label: "Minimum Consumption", minWidth: "120px", numeric: true },
    { key: "consumptionToUse", label: "Billable Amount", minWidth: "120px", numeric: true },
    { key: "usdAmount", label: "USD Amount", minWidth: "100px", numeric: true },
    { key: "status", label: "Status", minWidth: "120px" },
  ] as const;

  return (
    <div className="w-full max-w-full">
      <div className="w-full max-w-full overflow-x-auto border border-gray-200 rounded-md bg-white">
        <table className="w-full min-w-[1640px] text-sm">
          <thead>
            <tr className="bg-gray-50">
              {columns.map((col) => (
                <th
                  key={col.key}
                  className={`px-4 py-3 text-left font-medium text-gray-600 border-b border-r border-gray-200 cursor-pointer hover:bg-gray-100 ${(col as { numeric?: boolean }).numeric ? 'text-right' : ''}`}
                  style={{ minWidth: col.minWidth, position: 'sticky', left: col.key === 'merchantId' ? 0 : undefined, zIndex: col.key === 'merchantId' ? 10 : 1 }}
                  onClick={() => col.key !== 'status' && handleSort(col.key as SortColumn)}
                >
                  {col.label}
                  {col.key !== 'status' && renderSortIcon(col.key as SortColumn)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {getSortedData().map((merchant, index) => {
              const { status, variant } = getConsumptionStatus(
                merchant.realConsumption,
                merchant.minimumConsumption
              );
              const usdAmount = merchant.consumptionToUse / trmValue;

              return (
                <tr key={merchant.merchantId || `merchant-${index}`} className="hover:bg-gray-50">
                  <td
                    className="px-4 py-3 font-semibold text-blue-600 border-b border-r border-gray-200 whitespace-nowrap"
                    style={{ position: 'sticky', left: 0, backgroundColor: 'white', zIndex: 1 }}
                  >
                    {merchant.merchantId}
                  </td>
                  <td className="px-4 py-3 border-b border-r border-gray-200">
                    <div className="flex flex-col gap-0 items-start">
                      <span className="text-xs text-gray-600">{merchant.billingPeriod.startDate}</span>
                      <span className="text-xs text-gray-600">to {merchant.billingPeriod.endDate}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right font-semibold">{formatNumber(merchant.conversations)}</td>
                  <td className="px-4 py-3 text-right font-semibold">{formatNumber(merchant.callMinutes)}</td>
                  <td className="px-4 py-3 text-right font-semibold">{formatNumber(merchant.outbounds)}</td>
                  <td className="px-4 py-3 text-right text-sm">{formatCurrency(merchant.conversationCost)}</td>
                  <td className="px-4 py-3 text-right text-sm">{formatCurrency(merchant.callMinutesCost)}</td>
                  <td className="px-4 py-3 text-right text-sm">{formatCurrency(merchant.outboundCost)}</td>
                  <td className="px-4 py-3 text-right font-semibold" style={{ color: merchant.realConsumption > 0 ? '#16a34a' : '#6b7280' }}>
                    {formatCurrency(merchant.realConsumption)}
                  </td>
                  <td className="px-4 py-3 text-right text-sm text-gray-600">{formatCurrency(merchant.minimumConsumption)}</td>
                  <td className="px-4 py-3 text-right font-bold text-md">{formatCurrency(merchant.consumptionToUse)}</td>
                  <td className="px-4 py-3 text-right font-bold text-md text-green-600">{formatUSD(usdAmount)}</td>
                  <td className="px-4 py-3 border-b border-gray-200">
                    <Badge variant={variant}>{status}</Badge>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default RevenueDetailsTable;
